import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

const EditorContext = createContext(null)

export const useEditor = () => {
  const ctx = useContext(EditorContext)
  if (!ctx) throw new Error('useEditor must be used inside DataPipelineEditorProvider')
  return ctx
}

const stableStringify = (v) => {
  try { return JSON.stringify(v) } catch { return String(v) }
}

const safeJsonStringify = (v) => {
  if (v == null) return ''
  try { return JSON.stringify(v, null, 2) } catch { return String(v) }
}

function tryParse (raw) {
  if (raw == null || raw === '') return { ok: true, value: null }
  try { return { ok: true, value: JSON.parse(raw) } } catch (err) { return { ok: false, error: err.message } }
}

function DataPipelineEditorProvider ({ initialValue, children }) {
  const [code, setCode] = useState(initialValue.code || '')
  const [displayName, setDisplayName] = useState(initialValue.displayName || '')
  const [description, setDescription] = useState(initialValue.description || '')
  const [scope, setScope] = useState(initialValue.scope || 'client')
  const [pipelineText, setPipelineText] = useState(safeJsonStringify(initialValue.pipeline?.components || []))
  const [variables, setVariables] = useState(initialValue.pipeline?.variables || { filters: {}, dates: {} })
  // analysis is the field-schema description derived from a successful test
  // run (matches legacy behavior — only updated by Run). Included in the
  // dirty snapshot so Save enables after a Run; the backend treats analysis
  // as metadata and updates it in place without bumping the version.
  const [analysis, setAnalysis] = useState(initialValue.analysis || null)
  const [proposedPipelineText, setProposedPipelineText] = useState(null)
  const [testResult, setTestResult] = useState(null)
  const [testError, setTestError] = useState(null)
  const [testRunning, setTestRunning] = useState(false)

  // State (not ref) so markSaved re-runs the isDirty memo.
  const [savedSnapshot, setSavedSnapshot] = useState(() => stableStringify({
    code: initialValue.code || '',
    displayName: initialValue.displayName || '',
    description: initialValue.description || '',
    scope: initialValue.scope || 'client',
    pipelineText: safeJsonStringify(initialValue.pipeline?.components || []),
    variables: initialValue.pipeline?.variables || { filters: {}, dates: {} },
    analysis: initialValue.analysis || null
  }))

  const isDirty = useMemo(() => {
    return stableStringify({ code, displayName, description, scope, pipelineText, variables, analysis }) !== savedSnapshot
  }, [code, displayName, description, scope, pipelineText, variables, analysis, savedSnapshot])

  const markSaved = useCallback(() => {
    setSavedSnapshot(stableStringify({ code, displayName, description, scope, pipelineText, variables, analysis }))
  }, [code, displayName, description, scope, pipelineText, variables, analysis])

  const pipelineParse = useMemo(() => tryParse(pipelineText), [pipelineText])

  const proposeFromChat = useCallback((rawJson) => {
    try {
      const parsed = JSON.parse(rawJson)
      const components = Array.isArray(parsed) ? parsed : parsed?.components
      if (!Array.isArray(components)) return false
      setProposedPipelineText(JSON.stringify(components, null, 2))
      return true
    } catch {
      return false
    }
  }, [])

  const acceptProposal = useCallback(() => {
    if (proposedPipelineText == null) return
    setPipelineText(proposedPipelineText)
    setProposedPipelineText(null)
  }, [proposedPipelineText])

  const rejectProposal = useCallback(() => {
    setProposedPipelineText(null)
  }, [])

  const value = useMemo(() => ({
    code,
    setCode,
    displayName,
    setDisplayName,
    description,
    setDescription,
    scope,
    setScope,
    pipelineText,
    setPipelineText,
    pipelineParse,
    variables,
    setVariables,
    analysis,
    setAnalysis,
    proposedPipelineText,
    proposeFromChat,
    acceptProposal,
    rejectProposal,
    testResult,
    setTestResult,
    testError,
    setTestError,
    testRunning,
    setTestRunning,
    isDirty,
    markSaved
  }), [
    code, displayName, description, scope,
    pipelineText, pipelineParse, variables, analysis,
    proposedPipelineText, proposeFromChat, acceptProposal, rejectProposal,
    testResult, testError, testRunning,
    isDirty, markSaved
  ])

  return <EditorContext.Provider value={value}>{children}</EditorContext.Provider>
}

DataPipelineEditorProvider.propTypes = {
  initialValue: PropTypes.object.isRequired,
  children: PropTypes.node
}

export default DataPipelineEditorProvider
